import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { STORIES, RESOURCES } from "@/content/stories";
import { CATEGORIES } from "@/content/categories";
import { getStoryProgress } from "@/lib/progress";
import Icon from "@/components/shared/Icon";
import Seo from "@/components/shared/Seo";
import PageHero from "@/components/shared/PageHero";
import { printSheet } from "@/lib/print";

export default function Stories() {
  const { t, lp, code } = useLang();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const results = useMemo(
    () =>
      STORIES.filter((s) => {
        if (cat !== "all" && s.category !== cat) return false;
        if (q.trim()) {
          const text = `${s.title.en} ${s.title.zh} ${s.desc.en} ${s.desc.zh}`.toLowerCase();
          if (!text.includes(q.trim().toLowerCase())) return false;
        }
        return true;
      }),
    [q, cat]
  );

  const storyCats = CATEGORIES.filter((c) => STORIES.some((s) => s.category === c.id));

  return (
    <>
      <Seo title={t("stories.title")} description={t("stories.subtitle")} />
      <PageHero eyebrow={t("nav.stories")} title={t("stories.title")} subtitle={t("stories.subtitle")} />

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Icon name="Search" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={code === "zh" ? "搜索故事……" : "Search stories…"}
              data-testid="story-search-input"
              aria-label={t("common.search")}
              className="w-full rounded-2xl border-2 border-[#147DEB]/15 bg-white py-3 pl-12 pr-4 text-base text-[#082B66] outline-none focus:border-[#147DEB]"
            />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-2xl border-2 border-[#147DEB]/15 bg-white px-4 py-3 text-sm font-semibold text-[#082B66] outline-none focus:border-[#147DEB]" data-testid="story-filter-category">
            <option value="all">{t("common.all")}</option>
            {storyCats.map((c) => (
              <option key={c.id} value={c.id}>{c.name[code]}</option>
            ))}
          </select>
        </div>

        {results.length === 0 ? (
          <p className="mt-10 text-center text-base font-semibold text-[#475569]" data-testid="stories-empty">{t("common.noResults")}</p>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((s) => {
              const pct = getStoryProgress(s.slug);
              return (
                <Link key={s.slug} to={lp(`/stories/${s.slug}`)} data-testid={`story-card-${s.slug}`} className="group overflow-hidden rounded-3xl border-2 border-[#147DEB]/10 bg-white transition-all hover:-translate-y-1 hover:shadow-xl">
                  <div className="relative flex h-40 items-center justify-center" style={{ backgroundColor: s.cover }}>
                    <Icon name="BookOpen" className="h-16 w-16 text-white/90" />
                    <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-bold text-[#082B66]">{s.minutes} {t("common.min")}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-bold text-[#082B66]">{s.title[code]}</h3>
                    <p className="mt-2 text-sm text-[#475569]">{s.desc[code]}</p>
                    {pct > 0 && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs font-semibold text-[#64748B]"><span>{t("stories.readingProgress")}</span><span>{pct}%</span></div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#E8F5FF]"><div className="h-full rounded-full bg-[#45C878]" style={{ width: `${pct}%` }} /></div>
                      </div>
                    )}
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#147DEB]">{t("common.readStory")} <Icon name="ArrowRight" className="h-4 w-4" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Parent & Teacher resources */}
        <div className="mt-16 rounded-3xl border-2 border-[#45C878]/20 bg-gradient-to-br from-[#EAFBF1] to-white p-8">
          <h2 className="font-display flex items-center gap-2 text-2xl font-bold text-[#082B66]"><Icon name="GraduationCap" className="h-6 w-6 text-[#45C878]" /> {t("stories.parentResources")}</h2>
          <p className="mt-2 text-base text-[#475569]">{t("stories.resourcesDesc")}</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {RESOURCES.map((r) => (
              <div key={r.id} className="rounded-2xl border-2 border-[#147DEB]/10 bg-white p-5" data-testid={`resource-${r.id}`}>
                <Icon name="FileText" className="h-7 w-7 text-[#147DEB]" />
                <h3 className="font-display mt-3 font-bold text-[#082B66]">{r.title[code]}</h3>
                <p className="mt-1 text-sm text-[#64748B]">{r.desc[code]}</p>
                <button onClick={() => printSheet(r.title[code], r.desc[code], code)} data-testid={`resource-print-${r.id}`} className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[#E8F5FF] px-4 py-2 text-sm font-bold text-[#147DEB] hover:bg-[#D4EDFF]">
                  <Icon name="Printer" className="h-4 w-4" /> {t("stories.printSheet")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
